export type MiaolinCustomerDuplicateCheckField =
  | 'customer_code'
  | 'customer_short_name'
  | 'existing_customer_code'

export type MiaolinCustomerDuplicateCheckPurpose = 'duplicate_check' | 'existing_lookup'

export type MiaolinCustomerDuplicateCheckConfig = {
  enabled: boolean
  providerMode: 'webhook'
  providerState: 'configured' | 'not_configured'
  fields: MiaolinCustomerDuplicateCheckField[]
  fieldProviderStates?: Record<
    MiaolinCustomerDuplicateCheckField,
    'configured' | 'not_configured'
  >
}

export type MiaolinCustomerDuplicateCheckResult =
  | {
      status: 'duplicate_found' | 'not_found'
      field: MiaolinCustomerDuplicateCheckField
      value: string
      isDuplicate: boolean
      exists?: boolean
      purpose: MiaolinCustomerDuplicateCheckPurpose
      message: string
      matchedCustomerName?: string
      matchedCustomerCode?: string
      providerMode: 'webhook'
      providerState: 'configured'
    }
  | {
      status: 'found' | 'not_found'
      field: 'existing_customer_code'
      value: string
      isDuplicate: false
      exists: boolean
      purpose: 'existing_lookup'
      message: string
      matchedCustomerName?: string
      matchedCustomerCode?: string
      providerMode: 'webhook'
      providerState: 'configured'
    }
  | {
      status:
        | 'invalid'
        | 'provider_not_configured'
        | 'provider_token_not_configured'
        | 'webhook_http_error'
        | 'webhook_invalid_response'
        | 'webhook_fetch_failed'
      field: MiaolinCustomerDuplicateCheckField
      value: string
      isDuplicate: null
      exists?: null
      purpose: MiaolinCustomerDuplicateCheckPurpose
      message: string
      providerMode: 'webhook'
      providerState: 'not_configured' | 'configured' | 'failed'
    }

export const MIAOLIN_CUSTOMER_DUPLICATE_CHECK_CONFIG_PATH =
  '/api/public/customer-duplicate-check/config'
export const MIAOLIN_CUSTOMER_DUPLICATE_CHECK_PATH = '/api/public/customer-duplicate-check'

type CustomerDuplicateCheckFetchResponse = {
  ok: boolean
  status?: number
  json: () => Promise<unknown>
}

type CustomerDuplicateCheckFetcher = (
  input: string,
  init?: RequestInit,
) => Promise<CustomerDuplicateCheckFetchResponse>

type CustomerDuplicateCheckOptions = {
  fetcher?: CustomerDuplicateCheckFetcher
  signal?: AbortSignal
}

export function getCustomerDuplicateCheckFieldLabel(field: MiaolinCustomerDuplicateCheckField) {
  if (field === 'existing_customer_code') return '既有客戶代號'
  return field === 'customer_code' ? '客戶代號' : '客戶簡稱'
}

export function getCustomerDuplicateCheckPurpose(
  field: MiaolinCustomerDuplicateCheckField,
  purpose?: MiaolinCustomerDuplicateCheckPurpose,
): MiaolinCustomerDuplicateCheckPurpose {
  return purpose ?? (field === 'existing_customer_code' ? 'existing_lookup' : 'duplicate_check')
}

export function getCustomerDuplicateCheckActionLabel(
  field: MiaolinCustomerDuplicateCheckField,
  purpose?: MiaolinCustomerDuplicateCheckPurpose,
) {
  const label = getCustomerDuplicateCheckFieldLabel(field)
  return getCustomerDuplicateCheckPurpose(field, purpose) === 'existing_lookup'
    ? `比對${label}是否存在`
    : `比對${label}是否重複`
}

export async function fetchMiaolinCustomerDuplicateCheckConfig(
  options: CustomerDuplicateCheckOptions = {},
): Promise<MiaolinCustomerDuplicateCheckConfig> {
  const fetcher = options.fetcher ?? globalThis.fetch
  if (!fetcher) return createDisabledCustomerDuplicateCheckConfig()

  try {
    const response = await fetcher(MIAOLIN_CUSTOMER_DUPLICATE_CHECK_CONFIG_PATH, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: options.signal,
    })
    if (!response.ok) return createDisabledCustomerDuplicateCheckConfig()

    const payload = await response.json()
    const data =
      payload && typeof payload === 'object' && 'data' in payload ? payload.data : undefined
    if (!data || typeof data !== 'object') return createDisabledCustomerDuplicateCheckConfig()

    const enabled = getBooleanProperty(data, 'enabled')
    const providerState = getStringProperty(data, 'providerState')
    const fields = getCustomerDuplicateCheckFields(data)
    const fieldProviderStates = getCustomerDuplicateCheckFieldProviderStates(data)
    return {
      enabled,
      providerMode: 'webhook',
      providerState: enabled && providerState === 'configured' ? 'configured' : 'not_configured',
      fields,
      ...(fieldProviderStates ? { fieldProviderStates } : {}),
    }
  } catch {
    return createDisabledCustomerDuplicateCheckConfig()
  }
}

export async function checkMiaolinCustomerDuplicate(
  input: {
    field: MiaolinCustomerDuplicateCheckField
    purpose?: MiaolinCustomerDuplicateCheckPurpose
    value: string
    formId?: string
    questionId?: string
  },
  options: CustomerDuplicateCheckOptions = {},
): Promise<MiaolinCustomerDuplicateCheckResult> {
  const value = input.value.trim()
  const label = getCustomerDuplicateCheckFieldLabel(input.field)
  const purpose = getCustomerDuplicateCheckPurpose(input.field, input.purpose)
  if (!value) {
    return {
      status: 'invalid',
      field: input.field,
      value,
      isDuplicate: null,
      exists: null,
      purpose,
      message: `請先輸入${label}。`,
      providerMode: 'webhook',
      providerState: 'configured',
    }
  }

  const fetcher = options.fetcher ?? globalThis.fetch
  if (!fetcher) {
    return {
      status: 'webhook_fetch_failed',
      field: input.field,
      value,
      isDuplicate: null,
      exists: null,
      purpose,
      message: `${label}比對 API 目前無法呼叫。`,
      providerMode: 'webhook',
      providerState: 'failed',
    }
  }

  try {
    const response = await fetcher(MIAOLIN_CUSTOMER_DUPLICATE_CHECK_PATH, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        field: input.field,
        purpose,
        value,
        formId: input.formId,
        questionId: input.questionId,
      }),
      signal: options.signal,
    })
    const payload = await response.json()
    const data =
      payload && typeof payload === 'object' && 'data' in payload ? payload.data : undefined
    const code =
      payload &&
      typeof payload === 'object' &&
      'code' in payload &&
      typeof payload.code === 'string'
        ? payload.code
        : null
    const error =
      payload &&
      typeof payload === 'object' &&
      'error' in payload &&
      typeof payload.error === 'string'
        ? payload.error
        : ''

    if (!response.ok || !data || typeof data !== 'object') {
      console.error('[MiaoForm] customer duplicate check API failed', {
        status: response.ok ? 'invalid_envelope' : response.status,
        code,
        field: input.field,
        purpose,
      })
      return {
        status: normalizeCustomerDuplicateCheckFailureStatus(code),
        field: input.field,
        value,
        isDuplicate: null,
        exists: null,
        purpose,
        message: getFriendlyCustomerDuplicateCheckFailureMessage({
          label,
          purpose,
          code,
          fallbackMessage: error,
        }),
        providerMode: 'webhook',
        providerState: code === 'provider_not_configured' ? 'not_configured' : 'failed',
      }
    }

    if (purpose === 'existing_lookup' && input.field === 'existing_customer_code') {
      const responseStatus = getStringProperty(data, 'status')
      const exists =
        getBooleanProperty(data, 'exists') ||
        getBooleanProperty(data, 'isExistingCustomer') ||
        responseStatus === 'found' ||
        responseStatus === 'exists' ||
        responseStatus === 'existing_found'
      const status = exists ? 'found' : 'not_found'
      const message =
        getStringProperty(data, 'message') ||
        (exists ? `${label}已確認存在。` : `找不到${label}。`)
      return {
        status,
        field: input.field,
        value,
        isDuplicate: false,
        exists,
        purpose,
        message,
        matchedCustomerName: getCustomerCheckMatchedCustomerName(data),
        matchedCustomerCode: getCustomerCheckMatchedCustomerCode(data) || value,
        providerMode: 'webhook',
        providerState: 'configured',
      }
    }

    const isDuplicate = getBooleanProperty(data, 'isDuplicate')
    const message =
      getStringProperty(data, 'message') || getDefaultDuplicateCheckMessage(label, isDuplicate)
    return {
      status: isDuplicate ? 'duplicate_found' : 'not_found',
      field: input.field,
      value,
      isDuplicate,
      exists: !isDuplicate,
      purpose,
      message,
      matchedCustomerName: getCustomerCheckMatchedCustomerName(data),
      matchedCustomerCode: getCustomerCheckMatchedCustomerCode(data),
      providerMode: 'webhook',
      providerState: 'configured',
    }
  } catch (error) {
    console.error('[MiaoForm] customer duplicate check API request failed', {
      field: input.field,
      purpose,
      error,
    })
    return {
      status: 'webhook_fetch_failed',
      field: input.field,
      value,
      isDuplicate: null,
      exists: null,
      purpose,
      message: `${label}比對 API 失敗。`,
      providerMode: 'webhook',
      providerState: 'failed',
    }
  }
}

function createDisabledCustomerDuplicateCheckConfig(): MiaolinCustomerDuplicateCheckConfig {
  return {
    enabled: false,
    providerMode: 'webhook',
    providerState: 'not_configured',
    fields: [],
    fieldProviderStates: {
      customer_code: 'not_configured',
      customer_short_name: 'not_configured',
      existing_customer_code: 'not_configured',
    },
  }
}

function isCustomerDuplicateCheckField(value: unknown): value is MiaolinCustomerDuplicateCheckField {
  return (
    value === 'customer_code' ||
    value === 'customer_short_name' ||
    value === 'existing_customer_code'
  )
}

function getCustomerDuplicateCheckFields(source: object): MiaolinCustomerDuplicateCheckField[] {
  const value = (source as Record<string, unknown>).fields
  if (!Array.isArray(value)) return []
  return value.filter(isCustomerDuplicateCheckField)
}

function getCustomerDuplicateCheckFieldProviderStates(source: object) {
  const value = (source as Record<string, unknown>).fieldProviderStates
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const record = value as Record<string, unknown>
  return {
    customer_code: record.customer_code === 'configured' ? 'configured' : 'not_configured',
    customer_short_name:
      record.customer_short_name === 'configured' ? 'configured' : 'not_configured',
    existing_customer_code:
      record.existing_customer_code === 'configured' ? 'configured' : 'not_configured',
  } as const
}

function getBooleanProperty(source: object, key: string) {
  const value = (source as Record<string, unknown>)[key]
  return typeof value === 'boolean' ? value : false
}

function getStringProperty(source: object, key: string) {
  const value = (source as Record<string, unknown>)[key]
  return typeof value === 'string' ? value.trim() : ''
}

function getRecordProperty(source: object, key: string) {
  const value = (source as Record<string, unknown>)[key]
  return value && typeof value === 'object' && !Array.isArray(value) ? value : null
}

function getCustomerCheckMatchedCustomerName(source: object) {
  const customer = getRecordProperty(source, 'customer')
  return (
    getStringProperty(source, 'matchedCustomerName') ||
    getStringProperty(source, 'matched_customer_name') ||
    getStringProperty(source, 'customerShortName') ||
    getStringProperty(source, 'customer_short_name') ||
    getStringProperty(source, 'customerName') ||
    getStringProperty(source, 'customer_name') ||
    getStringProperty(source, 'name') ||
    (customer ? getStringProperty(customer, 'customer_short_name') : '') ||
    (customer ? getStringProperty(customer, 'customer_name') : '') ||
    (customer ? getStringProperty(customer, 'name') : '') ||
    undefined
  )
}

function getCustomerCheckMatchedCustomerCode(source: object) {
  const customer = getRecordProperty(source, 'customer')
  return (
    getStringProperty(source, 'matchedCustomerCode') ||
    getStringProperty(source, 'matched_customer_code') ||
    getStringProperty(source, 'customerCode') ||
    getStringProperty(source, 'customer_code') ||
    getStringProperty(source, 'code') ||
    (customer ? getStringProperty(customer, 'customer_code') : '') ||
    (customer ? getStringProperty(customer, 'code') : '') ||
    undefined
  )
}

function getFriendlyCustomerDuplicateCheckFailureMessage({
  label,
  purpose,
  code,
  fallbackMessage,
}: {
  label: string
  purpose: MiaolinCustomerDuplicateCheckPurpose
  code: string | null
  fallbackMessage: string
}) {
  const providerLabel = purpose === 'existing_lookup' ? '既有客戶代號' : label

  if (code === 'webhook_timeout') return `${providerLabel}比對逾時，請稍後再試。`
  if (
    code === 'provider_not_configured' ||
    code === 'provider_token_not_configured' ||
    code === 'webhook_invalid_response' ||
    code === 'webhook_http_error' ||
    code === 'webhook_fetch_failed'
  ) {
    return `${providerLabel}比對尚未正式運作，請稍後再試。`
  }

  const sanitizedFallback = fallbackMessage.trim()
  if (sanitizedFallback && !looksLikeTechnicalCustomerCheckMessage(sanitizedFallback)) {
    return sanitizedFallback
  }

  return `${providerLabel}比對暫時無法完成，請稍後再試。`
}

function looksLikeTechnicalCustomerCheckMessage(message: string) {
  return /Customer duplicate check|webhook response|invalid JSON|ApiEnvelope|API request failed/i.test(
    message,
  )
}

function normalizeCustomerDuplicateCheckFailureStatus(
  code: string | null,
): Extract<
  MiaolinCustomerDuplicateCheckResult['status'],
  | 'provider_not_configured'
  | 'provider_token_not_configured'
  | 'webhook_http_error'
  | 'webhook_invalid_response'
  | 'webhook_fetch_failed'
> {
  if (code === 'provider_not_configured') return 'provider_not_configured'
  if (code === 'provider_token_not_configured') return 'provider_token_not_configured'
  if (code === 'webhook_http_error') return 'webhook_http_error'
  if (code === 'webhook_invalid_response') return 'webhook_invalid_response'
  return 'webhook_fetch_failed'
}

function getDefaultDuplicateCheckMessage(label: string, isDuplicate: boolean) {
  return isDuplicate ? `此${label}已存在，請確認是否重複。` : `未找到重複${label}。`
}
